import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  RefreshControl,
  StatusBar,
  Platform,
} from 'react-native';

import SimpleHeader from '../../components/SimpleHeader';
import TabBar from '../../components/TabBar';
import AccountCard from '../../components/UIComponents/AccountCard';
import StockIcon from '../../components/UIComponents/StockIcon';
import HideableAccountRow from '../../components/UIComponents/HideableAccountRow';
import { useAuth } from '../../contexts/AuthContext';
import { useHiddenAccounts, keyForCurrent, keyForDeposit, keyForCard, keyForStock, keyForInvestment } from '../../contexts/HiddenAccountsContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useXStocks } from '../../hooks/useXStocks';
import { v2GetMyBalances, v2GetCard } from '../../services/v2/api';
import type { V2Balance, V2XStock } from '../../types/v2';
import { COLORS } from '../../theme/themeTokens';
import { getTokenIconSource , getLocalTokenIcon} from '../../tools/getCurrencyIcon';
import { setSupportPrefill } from '../../tools/supportPrefill';
import { formatDepositMeta } from '../../utils/depositMeta';
import { isStockAccountBalance, xStockTicker } from '../../utils/xstocks';
import { byUsdtValue, byUsdtValueUsdtFirst } from '../../utils/balanceOrder';

// Parse amount string to number, fallback 0
const parseAmount = (amount: string): number => {
  const num = parseFloat(amount);
  return isNaN(num) ? 0 : num;
};

// Format ISO date string (YYYY-MM-DD) to dd.mm.yy
const formatDepositDate = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y.slice(2)}`;
};

// Build deposit period subtitle from balance fields
const depositPeriodSubtitle = (balance: V2Balance): string | undefined => {
  const from = balance.deposit_date_from;
  const to = balance.deposit_date_to;
  if (from && to) { return `${formatDepositDate(from)} - ${formatDepositDate(to)}`; }
  if (from) { return `from ${formatDepositDate(from)}`; }
  if (to) { return `to ${formatDepositDate(to)}`; }
  return undefined;
};

/**
 * Merge balances that share the same token_symbol into a single synthetic entry
 * whose amount and value_usd are the sum of all merged entries.
 * All other fields (icon, name, etc.) are taken from the first balance in the group.
 */
const groupBalancesBySymbol = (bals: V2Balance[]): V2Balance[] => {
  const groups = new Map<string, V2Balance[]>();
  for (const b of bals) {
    const existing = groups.get(b.token_symbol);
    if (existing) {
      existing.push(b);
    } else {
      groups.set(b.token_symbol, [b]);
    }
  }
  return Array.from(groups.values()).map(group => {
    if (group.length === 1) { return group[0]; }
    const totalAmount = group.reduce((sum, b) => sum + parseAmount(b.amount), 0);
    const totalValueUsd = group.reduce((sum, b) => sum + parseAmount(b.value_usd ?? '0'), 0);
    // available_amount + locked_amount must also be summed across the
    // group — otherwise the "All assets" current row would pick
    // group[0]'s single-network values and display only one network's
    // funds. Mirrors the same fix in DashboardV2Screen.
    const totalAvailable = group.reduce(
      (sum, b) => sum + parseAmount(b.available_amount ?? b.amount),
      0,
    );
    const totalLocked = group.reduce(
      (sum, b) => sum + parseAmount(b.locked_amount ?? '0'),
      0,
    );
    return {
      ...group[0],
      amount: String(totalAmount),
      value_usd: String(totalValueUsd),
      available_amount: String(totalAvailable),
      locked_amount: String(totalLocked),
    };
  });
};

// `deposit` filter pill renders investment-coded accounts (UI:
// "Deposits"). `investment` pill renders t_fund-coded accounts
// (UI: "Investment") — the new subscription-fund surface added
// with backend migration 00096. Cards on the Investment tab render
// without an onPress for now: the dedicated detail screen for the
// fund UX is still pending sign-off.
// `xstocks` lists the user's tokenized stocks (design: first pill), held on
// xStocks accounts (one per stock). The rows are informational: no tap
// action — the stock screen opens from the Tokens catalogue.
type AssetFilter = 'xstocks' | 'current' | 'card' | 'deposit' | 'investment';

// Persisted across unmounts (PDF overlay replaces the whole render tree)
let persistedFilter: AssetFilter = 'current';

// What the client spends from first, then the cards, then what is put away.
// The Dashboard lists its sections in this same order; the two screens used
// to disagree, with xStocks leading here and trailing there.
//
// The last one is T-FUND, the name the Products screen has always used for
// it. "Investment Accounts" was the account type's own name leaking into a
// label, and it left one product called two things in one app.
const FILTERS: { key: AssetFilter; label: string }[] = [
  { key: 'current', label: 'Current accounts' },
  { key: 'card', label: 'Cards' },
  { key: 'xstocks', label: 'xStocks' },
  { key: 'deposit', label: 'Deposit Accounts' },
  { key: 'investment', label: 'T-FUND' },
];

interface AllAssetsScreenProps {
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onBalancePress?: (balance: V2Balance) => void;
  onCardPress?: (balance: V2Balance) => void;
  // onInvestmentPress — клик по плашке депозита (account_type='deposit')
  // открывает DepositAccountScreen для этого депозита. Зеркало того же
  // prop'а на DashboardV2Screen — App.tsx прокидывает один handler
  // handleOpenInvestmentDeposit на обоих экранах.
  onInvestmentPress?: (balance: V2Balance) => void;
  // onStockPress — клик по плашке акции открывает её график
  // (TokenDetailScreen), как это давно делает такая же плашка на
  // дашборде. Здесь она была единственной немой карточкой на экране.
  onStockPress?: (stock: V2XStock) => void;
}

const AllAssetsScreen: React.FC<AllAssetsScreenProps> = ({
  onBack,
  onNavigate,
  onBalancePress,
  onCardPress,
  onInvestmentPress,
  onStockPress,
}) => {
  const { isVisibleOnDashboard, getOverride, setOverride } = useHiddenAccounts();
  const xStocks = useXStocks();
  const snackbar = useSnackbar();
  const { firebaseUser, handleSessionExpired } = useAuth();
  // Status-bar inset for Android — same value CardScreen uses so the
  // title sits at the same vertical position. iOS gets 0 because the
  // App-level SafeAreaView already handles the notch.
  const statusBarHeight = Platform.OS === 'android' ? 28 : 0;

  const [balances, setBalances] = useState<V2Balance[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<AssetFilter>(persistedFilter);

  // Filter-bar auto-scroll: узкие экраны прячут крайние pills за правой
  // границей. При tap'е на такую pill scrollTo сдвигает содержимое так,
  // чтобы pill полностью попала в viewport (с 16 dp gutter'ом).
  const filterScrollRef = useRef<ScrollView>(null);
  const filterViewportWidthRef = useRef(0);
  const filterScrollXRef = useRef(0);
  const pillLayoutsRef = useRef<Record<string, { x: number; width: number }>>({});

  const handleSetFilter = (f: AssetFilter) => {
    persistedFilter = f;
    setActiveFilter(f);
    const layout = pillLayoutsRef.current[f];
    const viewport = filterViewportWidthRef.current;
    if (!layout || !filterScrollRef.current || viewport <= 0) return;

    const PADDING = 16;
    const pillLeft = layout.x;
    const pillRight = layout.x + layout.width;
    const currentLeft = filterScrollXRef.current;
    const currentRight = currentLeft + viewport;
    let targetX = currentLeft;
    if (pillRight > currentRight - PADDING) {
      targetX = pillRight - viewport + PADDING;
    } else if (pillLeft < currentLeft + PADDING) {
      targetX = pillLeft - PADDING;
    }
    targetX = Math.max(0, targetX);
    if (Math.abs(targetX - currentLeft) > 1) {
      filterScrollRef.current.scrollTo({ x: targetX, animated: true });
    }
  };

  // Card balance amounts fetched from Payoma via /api/v2/user/cards/:id
  // Key = wallet_address (card ID), Value = amount in major units
  const [cardAmounts, setCardAmounts] = useState<Record<string, number>>({});

  // Freeze the vertical ScrollView while a HideableAccountRow's horizontal
  // swipe gesture is in progress so the two axes don't compete for the
  // touch responder. Mirrors the same fix on DashboardV2Screen.
  const [scrollEnabled, setScrollEnabled] = useState(true);

  /** Fetch card balance amounts from Payoma for all card-type balances. */
  const fetchCardAmounts = useCallback(async (allBalances: V2Balance[]) => {
    const cards = allBalances.filter(
      b => (b.account_type_code === 'card' || b.account_type_code === 'card_balance') && b.wallet_address,
    );
    if (cards.length === 0) { return; }

    const results: Record<string, number> = {};
    await Promise.all(
      cards.map(async (b) => {
        try {
          const cardId = b.wallet_address!;
          const res = await v2GetCard(cardId);
          const walletBalance = res?.data?.wallet?.balance;
          const precision: number = res?.data?.wallet?.currency?.precision ?? 2;
          if (walletBalance && walletBalance.amount != null) {
            results[cardId] = walletBalance.amount / Math.pow(10, precision);
          }
        } catch (err) {
          console.warn('[AllAssetsScreen] Failed to fetch card amount for', b.wallet_address, err);
        }
      }),
    );
    setCardAmounts(prev => ({ ...prev, ...results }));
  }, []);

  const fetchBalances = useCallback(async () => {
    if (!firebaseUser) { return; }
    try {
      setLoading(true);
      setError(null);
      const response = await v2GetMyBalances();
      const fetchedBalances = response.balances || [];
      setBalances(fetchedBalances);
      // Fetch card amounts from Payoma in the background
      fetchCardAmounts(fetchedBalances);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load balances';
      console.error('[AllAssetsScreen] Failed to fetch balances:', err);
      if (
        errorMessage.includes('Session expired') ||
        errorMessage.includes('Not authenticated')
      ) {
        handleSessionExpired(errorMessage);
        return;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [firebaseUser, handleSessionExpired, fetchCardAmounts]);

  const onRefresh = useCallback(async () => {
    if (!firebaseUser) { return; }
    setRefreshing(true);
    try {
      setError(null);
      const response = await v2GetMyBalances();
      const fetchedBalances = response.balances || [];
      setBalances(fetchedBalances);
      fetchCardAmounts(fetchedBalances);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load balances';
      console.error('[AllAssetsScreen] Refresh failed:', err);
      if (
        errorMessage.includes('Session expired') ||
        errorMessage.includes('Not authenticated')
      ) {
        handleSessionExpired(errorMessage);
        return;
      }
      setError(errorMessage);
    } finally {
      setRefreshing(false);
    }
  }, [firebaseUser, handleSessionExpired, fetchCardAmounts]);

  useEffect(() => {
    if (!firebaseUser) {
      handleSessionExpired();
      return;
    }
    fetchBalances();
  }, [firebaseUser, handleSessionExpired, fetchBalances]);

  // Derived data — group by account_type_code (same as DashboardV2Screen)
  const isCurrentVirtual = (b: V2Balance) =>
    (!b.account_type_code || b.account_type_code === 'current') && b.provider_name === 'virtual';

  // Shares live on xStocks accounts, one per stock (backend migration 00124).
  //
  // An empty position is listed like an empty current account, which the
  // tab beside it has always done. Filtering on amount > 0 took the whole
  // pill away with the last share — and since the Dashboard hides an empty
  // holding by default and this screen is the only place to swipe it back,
  // a client who sold out could not reach their own account at all.
  const xStockBalances = xStocks.enabled
    ? balances.filter(isStockAccountBalance).sort(byUsdtValue)
    : [];

  const currentAccountBalances = balances
    .filter(b => isCurrentVirtual(b) && !(xStocks.enabled && xStocks.symbols.has(b.token_symbol)));

  const cardBalances = balances
    .filter(b => b.account_type_code === 'card' || b.account_type_code === 'card_balance')
    .sort(byUsdtValue);

  // После rename миграции 00098:
  //   account_type='deposit'    → "Deposits" pill (classic срочный)
  //   account_type='investment' → "Investments" pill (T-Fund-like)
  const investmentAccountBalances = balances
    .filter(b => b.account_type_code === 'deposit')
    .sort(byUsdtValue);

  const tFundAccountBalances = balances
    .filter(b => b.account_type_code === 'investment')
    .sort(byUsdtValue);

  // Group current account balances by token_symbol (e.g. USDT on Ethereum + USDT on Tron → one card)
  // Grouped first, ordered after: the tile shows USDT on Ethereum and on
  // Tron as one sum, and that sum is what it should be ordered by.
  const groupedCurrentAccountBalances = groupBalancesBySymbol(currentAccountBalances)
    .sort(byUsdtValueUsdtFirst);

  // Скрываем пиллы, для которых у юзера нет ни одного account'а. "current"
  // остаётся всегда — это базовый tab с placeholder'ом когда пусто.
  const visibleFilters = FILTERS.filter((f) => {
    switch (f.key) {
      case 'xstocks':    return xStockBalances.length > 0;
      case 'current':    return true;
      case 'card':       return cardBalances.length > 0;
      case 'deposit':    return investmentAccountBalances.length > 0;
      case 'investment': return tFundAccountBalances.length > 0;
      default:           return false;
    }
  });

  // Если активный пилл вдруг исчез (например админ удалил последний card
  // пока экран открыт) — сбрасываем на "current" чтобы не остаться на
  // невидимой вкладке.
  useEffect(() => {
    if (!visibleFilters.some((f) => f.key === activeFilter)) {
      handleSetFilter('current');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleFilters.length, activeFilter]);


  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={COLORS.pureLight} />

      <View style={{ paddingTop: statusBarHeight, backgroundColor: COLORS.white }}>
        <SimpleHeader title="All assets" onBack={onBack} backgroundVariant="white" />
      </View>

      {/* Filter pills — горизонтальный скролл чтобы умещались на узких
          экранах. paddingHorizontal на contentContainer держит первый/
          последний pill отступленным от краёв так же как в View-варианте.
          onLayout на pills + onScroll/onLayout на ScrollView'е питают
          handleSetFilter, которая при tap'е сдвигает частично видимую
          pill целиком в viewport. */}
      <ScrollView
        ref={filterScrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.filterBar}
        contentContainerStyle={styles.filterBarContent}
        onLayout={(e) => {
          filterViewportWidthRef.current = e.nativeEvent.layout.width;
        }}
        onScroll={(e) => {
          filterScrollXRef.current = e.nativeEvent.contentOffset.x;
        }}
        scrollEventThrottle={16}
      >
        {visibleFilters.map((f, index) => (
          <TouchableOpacity
            key={f.key}
            onLayout={(e) => {
              const { x, width } = e.nativeEvent.layout;
              pillLayoutsRef.current[f.key] = { x, width };
            }}
            style={[
              styles.filterPill,
              activeFilter === f.key && styles.filterPillActive,
              index > 0 && styles.filterPillSpacing,
            ]}
            onPress={() => handleSetFilter(f.key)}
            activeOpacity={0.7}
          >
            <Text
              style={[
                styles.filterPillText,
                activeFilter === f.key && styles.filterPillTextActive,
              ]}
            >
              {f.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        directionalLockEnabled
        scrollEnabled={scrollEnabled}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={COLORS.brandOrange}
            colors={[COLORS.brandOrange]}
            progressBackgroundColor={COLORS.primaryNavy}
          />
        }
      >
        {loading ? (
          <ActivityIndicator
            size="large"
            color={COLORS.brandOrange}
            style={styles.loadingIndicator}
          />
        ) : error ? (
          <TouchableOpacity onPress={fetchBalances} style={styles.retryRow}>
            <Text style={styles.retryText}>⚠ {error}{'  '}Retry</Text>
          </TouchableOpacity>
        ) : (
          <>
            {/* Current accounts (grouped by token_symbol).
                We always render every group here, even ones the user has
                hidden from the Dashboard — this screen is the single place
                they can restore visibility. Hidden rows are dimmed and
                expose an eye-open icon on swipe; visible rows expose
                eye-close. Tapping the icon (or swiping past ~half-width)
                toggles the hidden state. */}
            {activeFilter === 'current' && groupedCurrentAccountBalances.length > 0 && (
              <View style={styles.section}>
                {groupedCurrentAccountBalances.map(balance => {
                  const hideKey = keyForCurrent(balance.token_symbol);
                  // Default visibility: USDT is always pinned, other tokens
                  // only appear by default when non-zero. Explicit overrides
                  // ('hide' / 'show') still win — see HiddenAccountsContext.
                  const defaultVisible =
                    balance.token_symbol === 'USDT' || parseAmount(balance.amount) > 0;
                  // "Currently on Dashboard?" — drives swipe direction and toast wording.
                  const onDashboard = isVisibleOnDashboard(hideKey, defaultVisible);
                  return (
                    <HideableAccountRow
                      key={balance.token_symbol}
                      isHidden={!onDashboard}
                      swipeIcon="eye"
                      onSwipeStart={() => setScrollEnabled(false)}
                      onSwipeEnd={() => setScrollEnabled(true)}
                      onToggle={() => {
                        if (onDashboard) {
                          // Уже добавлено — ничего не делаем
                          return;
                        }
                        const prev = getOverride(hideKey) ?? null;
                        setOverride(hideKey, 'show');
                        snackbar.show({
                          message: 'Added to Dashboard',
                          actionLabel: 'Undo',
                          onAction: () => setOverride(hideKey, prev),
                        });
                      }}
                    >
                      <AccountCard
                        style={styles.card}
                        data={{
                          icon: getTokenIconSource(balance.token_symbol, balance.icon_url),
                          iconFallback: getLocalTokenIcon(balance.token_symbol),
                          name: `${balance.token_name}, ${balance.token_symbol}`,
                          valueUSDT: parseAmount(balance.value_usd ?? balance.amount),
                          value: parseAmount(balance.amount),
                          locked: parseAmount(balance.locked_amount ?? '0'),
                          changePercent: parseFloat(balance.change ?? '0'),
                          decimals: balance.token_decimals,
                        }}
                        showChangePercent={false}
                        showValueUSDT={balance.token_symbol !== 'USDT'}
                        onPress={() => onBalancePress?.(balance)}
                        spacious
                      />
                    </HideableAccountRow>
                  );
                })}
              </View>
            )}

            {/* xStocks holdings */}
            {activeFilter === 'xstocks' && xStockBalances.length > 0 && (
              <View style={styles.section}>
                {xStockBalances.map(balance => {
                  const stock = xStocks.stocks.find(s => s.symbol === balance.token_symbol);
                  const hideKey = keyForStock(balance.account_id ?? balance.id);
                  const onDashboard = isVisibleOnDashboard(hideKey, parseAmount(balance.amount) > 0);
                  return (
                    <HideableAccountRow
                      key={balance.id}
                      isHidden={!onDashboard}
                      swipeIcon="eye"
                      onSwipeStart={() => setScrollEnabled(false)}
                      onSwipeEnd={() => setScrollEnabled(true)}
                      onToggle={() => {
                        if (onDashboard) { return; }
                        const prev = getOverride(hideKey) ?? null;
                        setOverride(hideKey, 'show');
                        snackbar.show({
                          message: 'Added to Dashboard',
                          actionLabel: 'Undo',
                          onAction: () => setOverride(hideKey, prev),
                        });
                      }}
                    >
                      <AccountCard
                        style={styles.card}
                        data={{
                          iconNode: (
                            <StockIcon
                              symbol={balance.token_symbol}
                              iconUrl={stock?.icon_url ?? balance.icon_url}
                              size={32}
                            />
                          ),
                          name: xStockTicker(balance.token_symbol),
                          valueUSDT: parseAmount(balance.value_usd ?? '0'),
                          value: parseAmount(balance.amount),
                          locked: parseAmount(balance.locked_amount ?? '0'),
                          changePercent: parseFloat(stock?.change_24h ?? '0') || 0,
                          decimals: balance.token_decimals,
                        }}
                        percentSuffix="24h"
                        // Only when the catalogue knows the stock — without
                        // it there is no chart to open, and the tile stays
                        // as it was.
                        onPress={stock ? () => onStockPress?.(stock) : undefined}
                        disableShake={!stock}
                      />
                    </HideableAccountRow>
                  );
                })}
              </View>
            )}

            {/* Card accounts */}
            {activeFilter === 'card' && cardBalances.length > 0 && (
              <View style={styles.section}>
                {cardBalances.map(balance => {
                  const cardAmount = balance.wallet_address ? cardAmounts[balance.wallet_address] : undefined;
                  const hideKey = keyForCard(balance.account_id ?? balance.id);
                  // A card shows on the Dashboard until it is swiped away.
                  const onDashboard = isVisibleOnDashboard(hideKey, true);
                  return (
                    <HideableAccountRow
                      key={balance.id}
                      isHidden={!onDashboard}
                      swipeIcon="eye"
                      onSwipeStart={() => setScrollEnabled(false)}
                      onSwipeEnd={() => setScrollEnabled(true)}
                      onToggle={() => {
                        if (onDashboard) { return; }
                        const prev = getOverride(hideKey) ?? null;
                        setOverride(hideKey, 'show');
                        snackbar.show({
                          message: 'Added to Dashboard',
                          actionLabel: 'Undo',
                          onAction: () => setOverride(hideKey, prev),
                        });
                      }}
                    >
                      <AccountCard
                        style={styles.card}
                        data={{
                          icon: getTokenIconSource(balance.token_symbol, balance.icon_url),
                          iconFallback: getLocalTokenIcon(balance.token_symbol),
                          name: `${balance.token_name}, ${balance.token_symbol}`,
                          valueUSDT: parseAmount(balance.value_usd ?? balance.amount),
                          value: cardAmount ?? parseAmount(balance.amount),
                          locked: parseAmount(balance.locked_amount ?? '0'),
                          changePercent: parseFloat(balance.change ?? '0'),
                          decimals: balance.token_decimals,
                        }}
                        showValueUSDT={false}
                        showChangePercent={false}
                        onPress={() => onCardPress?.(balance)}
                        spacious
                      />
                    </HideableAccountRow>
                  );
                })}
              </View>
            )}

            {/* Deposits carry the same eye-swipe as current accounts: the
                full list lives here, and a row hidden from the Dashboard is
                dimmed until it is added back. */}
            {activeFilter === 'deposit' && investmentAccountBalances.length > 0 && (
              <View style={styles.section}>
                {investmentAccountBalances.map(balance => {
                  const hideKey = keyForDeposit(balance.account_id ?? balance.id);
                  const onDashboard = isVisibleOnDashboard(hideKey, parseAmount(balance.amount) > 0);
                  // Unified deposit caption — "USDT · 3 months · 3%".
                  // APY is already in the name line via formatDepositMeta,
                  // so the right-side percent is suppressed to avoid
                  // showing the rate twice.
                  const apyFraction = balance.apy ? parseFloat(balance.apy) : null;
                  return (
                    <HideableAccountRow
                      key={balance.id}
                      isHidden={!onDashboard}
                      swipeIcon="eye"
                      onSwipeStart={() => setScrollEnabled(false)}
                      onSwipeEnd={() => setScrollEnabled(true)}
                      onToggle={() => {
                        if (onDashboard) { return; }
                        const prev = getOverride(hideKey) ?? null;
                        setOverride(hideKey, 'show');
                        snackbar.show({
                          message: 'Added to Dashboard',
                          actionLabel: 'Undo',
                          onAction: () => setOverride(hideKey, prev),
                        });
                      }}
                    >
                      <AccountCard
                        style={styles.card}
                        data={{
                          icon: getTokenIconSource(balance.token_symbol, balance.icon_url),
                          iconFallback: getLocalTokenIcon(balance.token_symbol),
                          name: formatDepositMeta(balance.token_symbol, balance.term, apyFraction),
                          valueUSDT: parseAmount(balance.value_usd ?? balance.amount),
                          value: parseAmount(balance.amount),
                          locked: parseAmount(balance.locked_amount ?? '0'),
                          changePercent: 0,
                          decimals: balance.token_decimals,
                        }}
                        showValueUSDT={false}
                        showChangePercent={false}
                        subtitle={depositPeriodSubtitle(balance)}
                        // Tap на плашку открывает DepositAccountScreen —
                        // зеркало DashboardV2Screen.deposit-section. Без
                        // onPress AccountCard'у срабатывал shake-animation
                        // (псевдо-"вибрация") который пользователь не хотел.
                        onPress={() => onInvestmentPress?.(balance)}
                        spacious
                      />
                    </HideableAccountRow>
                  );
                })}
              </View>
            )}

            {activeFilter === 'investment' && tFundAccountBalances.length > 0 && (
              <View style={styles.section}>
                {tFundAccountBalances.map(balance => {
                  // Investment account-type tile. Tap открывает
                  // DepositAccountScreen — тот же handler что и для
                  // классического deposit'а (см. Dashboard tFund section
                  // для деталей). Name приоритезирует account.name,
                  // fallback на formatDepositMeta для legacy без name.
                  const apyFraction = balance.apy ? parseFloat(balance.apy) : null;
                  const hideKey = keyForInvestment(balance.account_id ?? balance.id);
                  // Like a card: the account exists whether or not it holds
                  // anything, so it shows until swiped away.
                  const onDashboard = isVisibleOnDashboard(hideKey, true);
                  return (
                    <HideableAccountRow
                      key={balance.id}
                      isHidden={!onDashboard}
                      swipeIcon="eye"
                      onSwipeStart={() => setScrollEnabled(false)}
                      onSwipeEnd={() => setScrollEnabled(true)}
                      onToggle={() => {
                        if (onDashboard) { return; }
                        const prev = getOverride(hideKey) ?? null;
                        setOverride(hideKey, 'show');
                        snackbar.show({
                          message: 'Added to Dashboard',
                          actionLabel: 'Undo',
                          onAction: () => setOverride(hideKey, prev),
                        });
                      }}
                    >
                    <AccountCard
                      style={styles.card}
                      data={{
                        icon: getTokenIconSource(balance.token_symbol, balance.icon_url),
                        iconFallback: getLocalTokenIcon(balance.token_symbol),
                        name: balance.account_name || formatDepositMeta(balance.token_symbol, balance.term, apyFraction),
                        valueUSDT: parseAmount(balance.value_usd ?? balance.amount),
                        value: parseAmount(balance.amount),
                        locked: parseAmount(balance.locked_amount ?? '0'),
                        changePercent: 0,
                        decimals: balance.token_decimals,
                      }}
                      showValueUSDT={false}
                      showChangePercent={false}
                      subtitle={depositPeriodSubtitle(balance)}
                      onPress={() => onInvestmentPress?.(balance)}
                      spacious
                    />
                    </HideableAccountRow>
                  );
                })}
              </View>
            )}

            {activeFilter === 'current' && groupedCurrentAccountBalances.length === 0 && (
              <Text style={styles.emptyText}>No accounts yet</Text>
            )}

            <View style={styles.bottomSpacer} />
          </>
        )}
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={screen => onNavigate(screen)} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  scrollView: {
    flex: 1,
    backgroundColor: COLORS.white,
    // borderTopLeftRadius: 12,
    // borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  filterBar: {
    // Горизонтальный ScrollView нельзя растягивать на flex:1, иначе он
    // съест место у нижнего content'а. flexGrow:0 + явная высота через
    // padding pill'ов даёт ровно ту высоту что была у View-варианта.
    flexGrow: 0,
    backgroundColor: COLORS.white,
  },
  filterBarContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 12,
    paddingBottom: 8,
    paddingLeft: 16,
    paddingRight: 16,
  },
  filterPill: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: COLORS.pureLight,
    alignItems: 'center',
  },
  filterPillSpacing: {
    marginLeft: 4,
  },
  filterPillActive: {
    backgroundColor: COLORS.steelNavy,
  },
  filterPillText: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.primaryNavy,
  },
  filterPillTextActive: {
    fontFamily: 'Inter',
    color: COLORS.white,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingTop: 20,
  },
  loadingIndicator: {
    marginTop: 40,
  },
  section: {
    marginBottom: 12,
  },
  sectionLabel: {
    fontSize: 18,
    fontWeight: '400',
    color: COLORS.blueSteel,
    marginBottom: 8,
  },
  card: {
    marginBottom: 12,
  },
  retryRow: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  retryText: {
    fontSize: 15,
    color: COLORS.brandOrange,
  },
  emptyText: {
    fontSize: 16,
    color: COLORS.blueSteel,
    textAlign: 'center',
    marginTop: 40,
  },
  getCardBtn: {
    width: '100%',
    backgroundColor: COLORS.brandOrange,
    borderRadius: 4,
    paddingVertical: 14,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
    marginBottom: 12,
  },
  openDepositBtn: {
    width: '100%',
    backgroundColor: '#C94021',
    borderRadius: 4,
    paddingVertical: 14,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
    marginBottom: 12,
  },
  getCardBtnText: {
    fontFamily: 'Inter',
    fontSize: 18,
    lineHeight: 22,
    fontWeight: '500',
    color: COLORS.white,
  },
  bottomSpacer: {
    height: 110,
  },
});

export default AllAssetsScreen;
